import type { BindingInfo } from "../registry/block-definition.js";
import type { SchemaScope } from "../expr/typecheck.js";
import { typeCheckAst } from "../expr/typecheck.js";
import { parseExpression } from "../expr/parser.js";
import { collectPaths } from "../expr/ast.js";
import { labelForPath } from "../data/sample.js";

/**
 * How an expression should be written where a person is going to read it:
 * `portfolio.allocation` as "Portfolio › Asset allocation", a piped value with
 * the filter named after it, a computed one marked with an ƒ.
 *
 * The canvas has always drawn its token chips this way. Sharing the same
 * function with the layer tree, the breadcrumb and the inspector is what stops
 * one part of the editor calling a thing "Asset allocation" while another calls
 * it "portfolio.allocation" — the raw path stays available as a tooltip, which
 * is the right place for it.
 */
export function describeExpression(expr: string, scope: SchemaScope): { label: string; type: BindingInfo["type"] } {
  try {
    const ast = parseExpression(expr);
    const tc = typeCheckAst(ast, scope);
    const type = tc.issues.length ? "unknown" : (tc.type as BindingInfo["type"]);
    const paths = collectPaths(ast);
    if (ast.kind === "path") return { label: labelForPath(ast.segments, scope), type };
    if (ast.kind === "pipe" && ast.input.kind === "path") return { label: `${labelForPath(ast.input.segments, scope)} · ${ast.name}`, type };
    // Anything computed — a conditional that omits an optional clause, a sum, a
    // fallback — is named after the fields it reads. Falling through to the raw
    // source printed `adviser.qualifications ? ", " + adv…` into a chip on the
    // page, which is the expression's text and not its meaning.
    const names = [...new Set(paths.map((p) => labelForPath(p, scope)))];
    if (names.length === 1) return { label: `ƒ ${names[0]}`, type };
    if (names.length > 1) return { label: `ƒ ${names.slice(0, 2).join(", ")}${names.length > 2 ? "…" : ""}`, type };
    return { label: expr, type };
  } catch {
    return { label: expr, type: "unknown" };
  }
}

/**
 * Rewrites the readable parts of a label written by a block: `{{ tokens }}` and
 * bare dotted paths become the names a person would use. Anything that does not
 * resolve is left exactly as it was, so an unknown path still shows itself
 * rather than turning into a guess.
 */
export function humaniseLabel(text: string, scope: SchemaScope): string {
  const described = (expr: string) => {
    const { label } = describeExpression(expr.trim(), scope);
    return label;
  };
  return text
    .replace(/\{\{([^}]+)\}\}/g, (_, expr: string) => described(expr))
    .replace(/\b[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)+\b/g, (path) => {
      const label = described(path);
      return label === path ? path : label;
    });
}
